import { useMemo } from 'react';

import {
  roundNumber,
  getMaxQuantityLimit,
  filterRemovedProducts,
  mapPriceExistsInBundle,
  getBundlePriceCalculations,
  combineBundleProducts,
  getBundlePriceTotals,
  hasMapSingleStrikethroughItem
} from '../utils/product-bundle-utils';
import usePriceAdjustment from './usePriceAdjustment';
import { getBundlePricing } from '../utils/productBundleData';

export const useBundlePricingDetails = ({
  product, products, loading: productsLoading, isCustomerIdentified = false, selectedAccessories = [],
  skipPriceAdjustment = false
}) => {
  const {
    priceUnitOfMeasure, showQuantityLimit
  } = product?.features ?? {};

  const bundleProducts = combineBundleProducts(products, selectedAccessories);
  const activeProducts = useMemo(() => filterRemovedProducts(bundleProducts), [products, selectedAccessories]);
  const adjustments = usePriceAdjustment(activeProducts, productsLoading, skipPriceAdjustment);
  const mapDetail = mapPriceExistsInBundle(activeProducts, isCustomerIdentified);
  const maxQuantityLimit = getMaxQuantityLimit(activeProducts);
  const priceTotals = getBundlePriceTotals(activeProducts);
  const hasMapSingleStrikethrough = hasMapSingleStrikethroughItem(activeProducts);

  const calculations = adjustments
    ? getBundlePriceCalculations({
        ...adjustments,
        firstPriceTotal: priceTotals?.firstPriceTotal,
        secondPriceTotal: priceTotals?.secondPriceTotal,
        valuePriceTotal: priceTotals?.valuePriceTotal
      })
    : null;

  const loading = !activeProducts?.length || productsLoading || !adjustments;

  const pricing = loading ? null : getBundlePricing(
    {
      originalPrice: calculations?.regularPrice,
      discount: calculations?.totalDollarOff,
      percent: calculations?.totalPercentOff,
      specialPrice: roundNumber({ number: calculations?.finalDiscountedPrice, decimal: 2 }),
      realFinalPrice: roundNumber({ number: calculations?.realFinalPrice, decimal: 2 }),
      mapDetail,
      quantityLimit: showQuantityLimit ? maxQuantityLimit?.quantityLimit : null,
      priceUnitOfMeasure,
      hasMapSingleStrikethrough
    }
  );

  return {
    loading,
    pricing,
    adjustments,
    mapDetail,
    calculations,
    maxQuantityLimit
  };
};
